import { useEffect, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MarketplaceServiceCard } from "@/components/marketplace/service-card";
import { fetchMarketplaceCategories, fetchMarketplaceServices, type MpServiceCard } from "@/lib/marketplace/queries";
import { isFeatured } from "@/lib/marketplace/promotions";
import { useT } from "@/i18n";
import { MP_GUIDES } from "@/lib/mp-guides";

const HERO_EXAMPLES = Array.from({ length: 8 }, (_, i) => `mpcopy.hero.example.${i + 1}`);

function ServiceRow({ title, query, to }: { title: string; query: Parameters<typeof fetchMarketplaceServices>[0]; to?: string }) {
  const { t } = useT();
  const { data } = useQuery({
    queryKey: ["mp-home-row", title, query],
    queryFn: () => fetchMarketplaceServices({ ...query, limit: 8 }),
  });
  if (!data?.length) return null;
  return (
    <section className="mt-10">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-display text-xl font-bold">{title}</h2>
        {to && (
          <Button asChild variant="ghost" size="sm">
            <Link to={to}>{t("mp.home.view_all")}</Link>
          </Button>
        )}
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {data.slice(0, 4).map((s: MpServiceCard) => (
          <MarketplaceServiceCard key={s.id} service={s} promoted={isFeatured(s.featured_until)} />
        ))}
      </div>
    </section>
  );
}

export function MarketplaceHome() {
  const { t } = useT();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [placeholder, setPlaceholder] = useState<string>(t("mpcopy.hero.search_placeholder"));

  useEffect(() => {
    let i = 0;
    const id = setInterval(() => {
      setPlaceholder(HERO_EXAMPLES.map((k) => t(k))[i % HERO_EXAMPLES.length] ?? t("mpcopy.hero.search_placeholder"));
      i += 1;
    }, 2600);
    return () => clearInterval(id);
  }, []);

  const { data: categories } = useQuery({ queryKey: ["mp-categories"], queryFn: fetchMarketplaceCategories });
  const topCategories = (categories ?? []).filter((c) => !c.parent_id);

  return (
    <div className="mx-auto max-w-6xl px-4 py-10">
      <section className="card-surface relative overflow-hidden p-8 text-center sm:p-12">
        <h1 className="mx-auto max-w-3xl font-display text-4xl font-bold sm:text-5xl">
          {t("mpcopy.hero.headline")}
        </h1>
        <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">{t("mpcopy.hero.sub")}</p>
        <form
          className="mx-auto mt-6 flex max-w-xl gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void navigate({ to: "/marketplace/explore", search: { q: search || undefined } });
          }}
        >
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={placeholder}
              className="pl-9"
            />
          </div>
          <Button type="submit" variant="hot">
            {t("mpcopy.hero.cta_primary")}
          </Button>
        </form>
        <div className="mt-4 flex justify-center">
          <Button asChild variant="outline">
            <Link to="/marketplace/sell">{t("mpcopy.hero.cta_secondary")}</Link>
          </Button>
        </div>
      </section>

      {topCategories.length > 0 && (
        <section className="mt-10">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-display text-xl font-bold">{t("mpcopy.nav.categories")}</h2>
            <Button asChild variant="ghost" size="sm">
              <Link to="/marketplace/categories">{t("mp.home.view_all")}</Link>
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {topCategories.map((c) => (
              <Link
                key={c.id}
                to="/marketplace/category/$slug"
                params={{ slug: c.slug }}
                className="card-surface p-4 text-center text-sm font-semibold transition-shadow hover:shadow-md"
              >
                {c.name}
              </Link>
            ))}
          </div>
        </section>
      )}

      <ServiceRow title={t("mpcopy.section.featured")} query={{ featuredOnly: true, sort: "hot" }} />
      <ServiceRow title={t("mpcopy.section.hot_now")} query={{ sort: "hot" }} to="/marketplace/hot" />
      <ServiceRow title={t("mpcopy.section.ambassador_picks")} query={{ ambassadorPick: true, sort: "hot" }} />
      <ServiceRow title={t("mpcopy.section.new_talent")} query={{ sort: "new" }} to="/marketplace/explore" />
      <ServiceRow title={t("mpcopy.section.top_rated")} query={{ sort: "rating" }} to="/marketplace/explore" />
      <ServiceRow title={t("mpcopy.section.fast_delivery")} query={{ sort: "fast" }} to="/marketplace/explore" />
      <ServiceRow title={t("mpcopy.section.under_50")} query={{ under50: true, sort: "price_asc" }} to="/marketplace/explore" />

      <section className="mt-10">
        <h2 className="font-display text-xl font-bold">{t("mpseo.hub.h1")}</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {MP_GUIDES.map((g) => (
            <Link
              key={g.slug}
              to="/marketplace/guides/$slug"
              params={{ slug: g.slug }}
              className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
            >
              {g.h1}
            </Link>
          ))}
          <Link
            to="/compare/marketplace"
            className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            {t("mpseo.compareOthers")}
          </Link>
        </div>
      </section>
    </div>
  );
}
